import moment from 'moment/moment';
import { ReactElement, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AdminPermission, BillingChannel, PlanFamily } from '@localstack/types';
import {
  ADMIN_CANCELLABLE_PLAN_FAMILIES,
  ADMIN_UPDATEABLE_PLAN_FAMILIES,
  SUB_ACTIVE_STATUSES,
} from '@localstack/constants';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Clear as ClearIcon } from '@mui/icons-material';

import {
  ButtonWithDialog,
  DangerZone,
  DangerZoneAction,
  DangerZoneActions,
  LoadingFragment,
  SubscriptionSummary,
  SubscriptionSummarySkeleton,
  PageTitle,
  getPeriodEnd,
} from '@localstack/ui';

import { AdminService, ComputeService, formatDate, useApiEffect, useApiGetter, useRoutes } from '@localstack/services';

import { AppRoute } from '~/config';
import { AdminLayout } from '~/layouts';

import { useAuthProvider } from '~/hooks/useAuthProvider';

import { SubscriptionFeaturesEditor } from './SubscriptionFeaturesEditor';

export const Subscription = (): ReactElement => {
  const { can } = useAuthProvider();
  const { goto } = useRoutes();
  const { orgId, subscriptionId } = useParams<'orgId' | 'subscriptionId'>() as {
    orgId: string;
    subscriptionId: string;
  };

  const [cancelAt, setCancelAt] = useState<number | null>(null);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState<boolean>(false);
  const [reactivateSubscription, setReactivateSubscription] = useState<boolean>(false);

  const { data: subscription } = useApiGetter(AdminService, 'getOrganizationSubscription', [orgId, subscriptionId]);
  const { data: usage } = useApiGetter(AdminService, 'getOrganizationSubscriptionUsage', [orgId, subscriptionId]);
  const { data: organization } = useApiGetter(AdminService, 'getOrganization', [orgId]);
  const { rescheduleOrganizationSubscription, isLoading: isSubRescheduling } = useApiEffect(
    AdminService,
    ['rescheduleOrganizationSubscription'],
    { revalidate: ['getOrganizationSubscription'] },
  );
  const { data: licenseAssignments, isLoading: isLicensesLoading } = useApiGetter(
    AdminService,
    'listOrganizationLicenseAssignments',
    [organization?.id || ''],
    { enable: !!organization },
  );

  const isCancelling = !!(subscription?.cancel_at || subscription?.cancel_at_period_end);
  const isTrial = subscription?.plan.family === PlanFamily.TRIAL_PLANS;
  const isInternal = subscription?.id.startsWith('lsub_'); // TODO: find a better way
  const isActive = subscription && SUB_ACTIVE_STATUSES.includes(subscription.status);

  const isCancelableFamily = subscription && ADMIN_CANCELLABLE_PLAN_FAMILIES.includes(subscription.plan.family);
  const isUpdatablePlanFamily = subscription && ADMIN_UPDATEABLE_PLAN_FAMILIES.includes(subscription.plan.family);

  // note trials are always in "cancelling" state

  const isModifiable =
    subscription?.plan?.family &&
    isActive &&
    (!isCancelling || (isCancelling && isTrial)) &&
    subscription.billing_channel != BillingChannel.AWS_MARKETPLACE;
  const isCancellable = isModifiable && isCancelableFamily;
  const isUpdateable = isModifiable && isUpdatablePlanFamily;
  const isReschedulable = isActive || isInternal;

  const { cancelOrganizationSubscription } = useApiEffect(AdminService, ['cancelOrganizationSubscription'], {
    revalidate: ['listOrganizationSubscriptions', 'getOrganizationSubscription'],
  });

  const { data: computeUsage } = useApiGetter(ComputeService, 'getComputeUsage', []);

  useEffect(() => {
    if (!subscription) return;

    // update rescheduling data
    setCancelAt(subscription.cancel_at || null);
    setCancelAtPeriodEnd(subscription.cancel_at_period_end || false);
  }, [subscription]);

  const cancelAtChanged = subscription?.cancel_at !== cancelAt;
  const cancelAtPeriodEndChanged = subscription?.cancel_at_period_end !== cancelAtPeriodEnd;

  return (
    <AdminLayout
      documentTitle="Subscription Details"
      title={
        <PageTitle
          title={`Organization ${orgId}`}
          breadcrumbs={[
            ['Organizations', () => goto(AppRoute.ADMIN_ORGANIZATIONS)],
            [orgId, () => goto(AppRoute.ADMIN_ORGANIZATION, { id: orgId })],
            ['Subscriptions', () => goto(AppRoute.ADMIN_ORGANIZATION, { id: orgId })],
            [subscription?.plan?.name ?? subscriptionId, () => null],
          ]}
        />
      }
    >
      <Grid container spacing={3}>
        <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
          <Card>
            <CardHeader title="Subscription Details" />
            {(!subscription || isSubRescheduling || isLicensesLoading) && (
              <>
                <SubscriptionSummarySkeleton />
                <CardContent>
                  <LoadingFragment loading height={165} variant="card" />
                </CardContent>
              </>
            )}
            {subscription && !isSubRescheduling && !isLicensesLoading && (
              <>
                <SubscriptionSummary
                  subscription={subscription}
                  usage={usage}
                  licenseAssignments={licenseAssignments ?? []}
                  computeUsage={computeUsage}
                />
                {can(AdminPermission.WORKSPACES_WRITE) && (
                  <CardContent>
                    <DangerZone>
                      <DangerZoneActions>
                        {isUpdateable && (
                          <DangerZoneAction
                            primaryText="Update Subscription"
                            secondaryText="Upgrade or downgrade Subscription"
                            actionText="Update"
                            onClick={() =>
                              goto(AppRoute.ADMIN_ORGANIZATION_SUBSCRIPTION_UPDATE, { orgId, subscriptionId })
                            }
                          />
                        )}
                        {isCancellable && (
                          <ButtonWithDialog
                            componentType="DangerZoneAction"
                            primaryText="Cancel Subscription"
                            secondaryText="Cancel this Subscription by the End of Prepaid Period"
                            actionText="Cancel"
                            onClick={async () => {
                              await cancelOrganizationSubscription(orgId, subscriptionId);
                              goto(AppRoute.ADMIN_ORGANIZATION, { id: orgId });
                            }}
                            dialogTitle="Cancel Subscription"
                            cancelButtonText="Don't Cancel"
                            confirmButtonText="Cancel Subscription"
                            dialogContent="Are you sure you want to cancel this Subscription?"
                          />
                        )}
                        {/* TODO: move to its own component? */}
                        {isReschedulable && (
                          <ButtonWithDialog
                            componentType="DangerZoneAction"
                            primaryText="Reschedule Subscription"
                            secondaryText="Set or update date until when Subscription is active"
                            actionText="Reschedule"
                            onClick={
                              // TODO: generate types/clients with nullable props, as they are
                              //       essentially nullable and not just optional/undefined!
                              () =>
                                rescheduleOrganizationSubscription(orgId, subscriptionId, {
                                  cancel_at: (cancelAtChanged ? cancelAt : undefined) as number | undefined,
                                  cancel_at_period_end: cancelAtPeriodEndChanged ? cancelAtPeriodEnd : undefined,
                                  reactivate: reactivateSubscription,
                                })
                            }
                            dialogTitle="Reschedule Subscription"
                            cancelButtonText="Cancel"
                            confirmButtonText="Reschedule Subscription"
                            disableConfirmButton={
                              !!(cancelAt && cancelAtPeriodEnd) || (!cancelAt && !cancelAtPeriodEnd && isTrial)
                            }
                            dialogContent={
                              <>
                                {!cancelAt && !cancelAtPeriodEnd && (
                                  <Box mb={2}>
                                    <Alert severity="warning">
                                      <Typography paragraph>
                                        Any existing cancellation date will be removed. If the subscription is scheduled
                                        for cancellation the subscription will be moved to an active state again, unless
                                        unpaid.
                                      </Typography>
                                      <Typography paragraph>
                                        If you would like the subscription to maintain the same cancellation period,
                                        make sure to re-add it below by either cancelling at the period end or on a
                                        specific date.
                                      </Typography>
                                    </Alert>
                                  </Box>
                                )}
                                {!isActive && isInternal && (
                                  <FormControlLabel
                                    label="Reactivate Subscription"
                                    control={
                                      <Switch
                                        color="primary"
                                        checked={reactivateSubscription}
                                        onChange={(_e, checked) => setReactivateSubscription(checked)}
                                      />
                                    }
                                  />
                                )}
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                  <FormControlLabel
                                    label="Cancel At Period End"
                                    control={
                                      <Switch
                                        color="primary"
                                        checked={cancelAtPeriodEnd}
                                        onChange={(_e, checked) => setCancelAtPeriodEnd(checked)}
                                      />
                                    }
                                  />
                                  <Box p={2}>
                                    <Typography>OR</Typography>
                                  </Box>
                                  <TextField
                                    required
                                    variant="outlined"
                                    label="Cancel At a Custom Date"
                                    size="medium"
                                    type="date"
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="start">
                                          <IconButton onClick={() => setCancelAt(null)} size="large">
                                            <ClearIcon />
                                          </IconButton>
                                        </InputAdornment>
                                      ),
                                    }}
                                    onChange={({ target }) =>
                                      setCancelAt(target.value ? moment(target.value).unix() : null)
                                    }
                                    value={cancelAt ? moment.unix(cancelAt).format(moment.HTML5_FMT.DATE) : ''}
                                  />
                                </Box>
                                {(cancelAt || cancelAtPeriodEnd) && (
                                  <Typography paragraph variant="caption" color="error" align="right">
                                    Subscription will be cancelled on{' '}
                                    <strong>{formatDate(cancelAt || getPeriodEnd(subscription))}</strong>
                                  </Typography>
                                )}
                              </>
                            }
                          />
                        )}
                      </DangerZoneActions>
                    </DangerZone>
                  </CardContent>
                )}
              </>
            )}
          </Card>
        </Grid>
        <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
          <SubscriptionFeaturesEditor disableInteraction={!can(AdminPermission.WORKSPACES_WRITE)} />
          <Box mt={3} />
        </Grid>
      </Grid>
    </AdminLayout>
  );
};
